import { useMemo, useState } from "react";

import { useSelectionStore } from "../../state/useSelectionStore";
import { useLayerStore } from "../../state/useLayerStore";
import { RADIUS_CHOICES, useRisksStore } from "../../state/useRisksStore";
import { useAreaRisks, usePointRisks } from "../../lib/useRisks";
import {
  CLAY_LABELS,
  CLAY_STUDY_REQUIRED_FROM,
  PERIL_MEANING,
  RADON_LABELS,
  SEISMIC_LABELS,
  type PerilId,
} from "../../lib/risks";
import { num } from "../../lib/format";
import { levelDef } from "../../map/levels";
import { RISK_METRICS, type RiskMetricId } from "../../map/layers/risks";
import { Choice, Figure, SectionTitle, Select } from "../ui/Field";
import { CatnatTimeline, PerilKey } from "./CatnatTimeline";

/**
 * What the ground and the weather have already done here.
 *
 * Ordered by how much a fact should move a decision, which is not the order a
 * hazard map would use. The declaration record comes first: it is the only
 * thing on this pane that says something *happened* rather than that something
 * is possible, and ten drought declarations in fifteen years is a stronger
 * statement than any modelled class. The regulated zonings follow, then the
 * inventory of what is nearby.
 *
 * This is not an état des risques. That document has a prescribed form and
 * producing something that resembles it takes on liability; everything here is
 * informational, and the link at the bottom goes to the source a listing is
 * already required to reference.
 */
export function RisksPanel({ visible }: { visible: boolean }) {
  const selection = useSelectionStore((s) => s.selection);
  const metric = useRisksStore((s) => s.metric);
  const recentOnly = useRisksStore((s) => s.recentOnly);
  const radius = useRisksStore((s) => s.radius);
  const [timeline, setTimeline] = useState<PerilId | "all">("all");

  const area = useMemo(
    () => (selection ? { scope: selection.scope, code: selection.code } : null),
    [selection],
  );
  const point = useMemo(
    () => (selection ? { lon: selection.point.lon, lat: selection.point.lat } : null),
    [selection],
  );

  const { isLoading, isError, data, perils, quietFor } = useAreaRisks(area);
  const spot = usePointRisks(point, radius);

  const declaredPerils = perils.map((p) => p.peril);
  const clay = spot.data?.clay ?? null;
  const official = spot.data?.clay_official ?? null;
  // Géorisques' own point classification is finer than our polygon and is what
  // the regulated document quotes, so it wins where both answer — but its label
  // arrives in French, so only the code is taken and the wording stays ours.
  const clayClass = official ? Number(official.codeExposition) : (clay?.niveau ?? null);

  return (
    <div className="px-4 py-3">
      <MapControls metric={metric} recentOnly={recentOnly} />

      <div className="mt-4 border-t border-line pt-3">
        {!data ? (
          <p className="rounded-lg bg-surface-alt px-3 py-3 text-center text-[12px] leading-snug text-ink-soft">
            {isLoading
              ? "Loading…"
              : isError
                ? "No hazard record for this area."
                : "Click an area on the map."}
          </p>
        ) : (
          <>
            <h3 className="truncate text-[13px] font-semibold">{selection?.name}</h3>
            <p className="text-[10px] text-ink-soft">
              {levelDef(data.scope === "departement" ? "departement" : "commune").noun}
              {data.resolved_from === "iris" &&
                " · hazards are declared per city, not per neighbourhood"}
              {!data.exact && " · averaged across the department"}
            </p>

            <div className="mt-3 grid grid-cols-3 gap-2">
              <Figure
                label="Declared"
                value={num(data.summary.cn_total)}
                sub="since 1982"
                hint="Every arrêté de catastrophe naturelle covering this area since the 1982 law."
              />
              <Figure
                label="Last 20 yrs"
                value={num(data.summary.cn20_total ?? 0)}
                sub="declarations"
                hint="Recent record. A commune that stopped flooding in the 1990s reads very differently here."
              />
              <Figure
                label="Most recent"
                value={data.summary.cn_last_year ? String(data.summary.cn_last_year) : "—"}
                sub={quietFor === null ? "never" : quietFor === 0 ? "this year" : `${quietFor} yrs ago`}
                accent={quietFor !== null && quietFor <= 3}
              />
            </div>

            {data.declarations.length > 0 && (
              <div className="mt-4 border-t border-line pt-3">
                <SectionTitle>What has been declared</SectionTitle>
                <Choice
                  label="Timeline"
                  value={timeline}
                  options={[
                    { value: "all" as const, label: "All" },
                    ...declaredPerils.slice(0, 2).map((p) => ({
                      value: p,
                      label: p === "inondation" ? "Flood" : p === "secheresse" ? "Drought" : "Ground",
                    })),
                  ]}
                  onChange={setTimeline}
                />
                {visible && (
                  <div className="mt-2">
                    <CatnatTimeline declarations={data.declarations} peril={timeline} />
                    <PerilKey perils={timeline === "all" ? declaredPerils : [timeline]} />
                  </div>
                )}

                <ul className="mt-3 space-y-1.5">
                  {perils.map((row) => (
                    <li key={row.peril} className="flex items-baseline gap-2" title={PERIL_MEANING[row.peril]}>
                      <span className="min-w-0 flex-1 truncate text-[12px]">{row.label}</span>
                      <span className="shrink-0 text-[12px] font-semibold tabular-nums">
                        {num(row.total)}
                      </span>
                      <span className="w-24 shrink-0 text-right text-[10px] tabular-nums text-ink-soft">
                        {row.recent} in 20 yrs
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>Under this exact spot</SectionTitle>
              <div className="grid grid-cols-2 gap-2">
                <Figure
                  label="Clay shrink-swell"
                  value={
                    clayClass
                      ? CLAY_LABELS[clayClass] ?? official?.exposition ?? "—"
                      : spot.isLoading
                        ? "…"
                        : "None mapped"
                  }
                  sub={
                    clayClass && clayClass >= CLAY_STUDY_REQUIRED_FROM
                      ? "study required on a land sale"
                      : "BRGM exposure map"
                  }
                  accent={!!clayClass && clayClass >= CLAY_STUDY_REQUIRED_FROM}
                  hint="Ground that swells and shrinks with the seasons. The largest single cause of structural damage claims on French houses."
                />
                <Figure
                  label="Radon"
                  value={
                    data.summary.radon ? RADON_LABELS[data.summary.radon] ?? "—" : "—"
                  }
                  sub="IRSN potential class"
                  hint="Potential from the geology beneath the commune, not a measurement in any building."
                />
                <Figure
                  label="Seismic"
                  value={
                    data.summary.seismic ? SEISMIC_LABELS[data.summary.seismic] ?? "—" : "—"
                  }
                  sub="regulatory zone"
                  hint="The zoning that sets construction rules. All of Île-de-France sits in the lowest band."
                />
                <Figure
                  label="Prevention plan"
                  value={
                    data.summary.ppr_count
                      ? data.summary.ppr_approved
                        ? "Approved"
                        : "Prescribed"
                      : "None"
                  }
                  sub={data.summary.ppr_risks ? String(data.summary.ppr_risks).slice(0, 28) : "no PPR here"}
                  hint="A PPR brings building restrictions — and its absence raises the CatNat deductible after a disaster."
                />
              </div>
            </div>

            <div className="mt-4 border-t border-line pt-3">
              <SectionTitle>Within {radius >= 1000 ? `${radius / 1000} km` : `${radius} m`}</SectionTitle>
              <Select
                label="Search radius"
                value={radius}
                options={RADIUS_CHOICES.map((r) => ({
                  value: r,
                  label: r >= 1000 ? `${r / 1000} km` : `${r} m`,
                }))}
                onChange={useRisksStore.getState().setRadius}
                hint="Hazards near a boundary sit in the neighbouring commune, so this searches by distance rather than by administrative area."
              />
              <div className="mt-2 grid grid-cols-3 gap-2">
                <Nearby label="Cavities" value={spot.data?.cavities} loading={spot.isLoading} />
                <Nearby label="Plants" value={spot.data?.installations} loading={spot.isLoading} />
                <Nearby label="Polluted" value={spot.data?.polluted_sites} loading={spot.isLoading} />
              </div>
            </div>

            <p className="mt-4 border-t border-line pt-2 text-[10px] leading-snug text-ink-soft">
              Informational only — this is not an état des risques, which is the
              regulated document a seller owes you. Check the address itself on{" "}
              <a
                className="text-accent hover:underline"
                href={`https://www.georisques.gouv.fr/mes-risques/connaitre-les-risques-pres-de-chez-moi/rapport?city=${encodeURIComponent(
                  selection?.name ?? "",
                )}&codeInsee=${data.code}`}
                target="_blank"
                rel="noreferrer"
              >
                Géorisques
              </a>
              . Hazard exposure and insurability are different questions: the
              CatNat regime socialises most of this, but deductibles rise where a
              commune has no prevention plan.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * A live count from Géorisques.
 *
 * `null` and `0` mean opposite things here — one is "we could not ask", the
 * other is "we asked and there are none" — so they get different words.
 */
function Nearby({
  label,
  value,
  loading,
}: {
  label: string;
  value: number | null | undefined;
  loading: boolean;
}) {
  return (
    <Figure
      label={label}
      value={loading ? "…" : value == null ? "—" : num(value)}
      sub={value == null && !loading ? "unavailable" : "recorded"}
    />
  );
}

/** What the map is shaded by. Kept at the top: it changes what you are looking at. */
function MapControls({ metric, recentOnly }: { metric: RiskMetricId; recentOnly: boolean }) {
  const setMetric = useRisksStore((s) => s.setMetric);
  const setRecentOnly = useRisksStore((s) => s.setRecentOnly);
  const isVisible = useLayerStore((s) => s.isVisible);
  const setVisible = useLayerStore((s) => s.setVisible);
  const unavailable = useLayerStore((s) => s.unavailable);

  const missing = unavailable.includes("catnat");

  return (
    <section>
      <div className="grid grid-cols-2 gap-2">
        <Select
          label="Map shows"
          value={metric}
          options={Object.entries(RISK_METRICS).map(([id, m]) => ({
            value: id as RiskMetricId,
            label: m.label,
          }))}
          onChange={setMetric}
        />
        <Select
          label="Counting"
          value={recentOnly ? "recent" : "all"}
          options={[
            { value: "all", label: "Since 1982" },
            { value: "recent", label: "Last 20 years" },
          ]}
          onChange={(v) => setRecentOnly(v === "recent")}
          hint="A commune that flooded repeatedly until the 1990s and one still flooding look identical over the full record."
        />
      </div>
      {missing ? (
        <p className="mt-1.5 text-[10px] text-ink-soft">
          Hazard tiles not built — run <code>make georisques</code>.
        </p>
      ) : (
        !isVisible("catnat") && (
          <button
            onClick={() => setVisible("catnat", true)}
            className="mt-1.5 text-[10px] font-medium text-accent hover:underline"
          >
            Show it on the map
          </button>
        )
      )}
    </section>
  );
}
